// src/pages/trading-portal/TradeExecution/AIAgentSimulatorPage.js
// AI Agent Simulator: fake market data over a range of dates for one of the team's AI agents,
// and the agent run across them in one go (positions carry overnight, results come by date).
//
// The editor, the run, the replay and the bar inspector are the agent tab's Data Simulator
// (AIAgentDataSimulator) as `variant="multiDate"`, on this module's own routes and scenarios: the
// tab's single-date scenarios are not listed here, nor these there.
//
// The server checks this module's permission for each action, and view permission for AI Trading
// Agents too (a run executes the agent's rules and prompts and shows their traces). The page checks
// the same, so a missing one reads as a message instead of failed requests.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Grid, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, Skeleton,
} from '@mui/material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import AIAgentDataSimulator from './AIAgentDataSimulator';
import { buildMarketDataSections, MarketDataSectionGrid, resolveEnabledAgents } from './MarketDataSections';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const MODULE_SLUG = 'tr-tee-ai-agent-simulator';
const AGENTS_SLUG = 'tr-tee-ai-agents';
// The agent list in one page: the route's default page is 10 agents.
const AGENT_LIMIT = 500;

const AIAgentSimulatorPage = () => {
    const [perms, setPerms] = useState(null);           // {view, agents, add, edit, del} once checked
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(true);
    const [agentId, setAgentId] = useState('');
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState(null);

    useEffect(() => {
        (async () => {
            // One after another: the first fills the permission cache the others read.
            const view = await hasPermission(MODULE_SLUG, 'view');
            const agentsView = await hasPermission(AGENTS_SLUG, 'view');
            const add = await hasPermission(MODULE_SLUG, 'add');
            const edit = await hasPermission(MODULE_SLUG, 'edit');
            const del = await hasPermission(MODULE_SLUG, 'delete');
            setPerms({ view, agents: agentsView, add, edit, del });
        })();
    }, []);

    const allowed = Boolean(perms?.view && perms?.agents);

    const loadAgents = useCallback(async () => {
        setAgentsLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('/api/trade-execution/ai-agents', { params: { page: 1, limit: AGENT_LIMIT } });
            const list = res.data?.data || [];
            setAgents(list);
            setAgentId((prev) => (list.some((a) => a.id === prev) ? prev : (list[0]?.id ?? '')));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load agents');
        } finally {
            setAgentsLoading(false);
        }
    }, []);

    useEffect(() => { if (allowed) loadAgents(); }, [allowed, loadAgents]);

    const agent = agents.find((a) => a.id === agentId) || null;

    if (!perms) {
        return <Box sx={{ p: 3 }}><Skeleton variant="rectangular" height={160} sx={{ borderRadius: '12px' }} /></Box>;
    }
    if (!perms.view) {
        return <Alert severity="warning" sx={{ fontFamily }}>You do not have permission to view the AI Agent Simulator.</Alert>;
    }
    if (!perms.agents) {
        return (
            <Alert severity="warning" sx={{ fontFamily }}>
                The AI Agent Simulator runs an agent&apos;s own rules and prompts, so it also needs view permission for AI Trading Agents.
            </Alert>
        );
    }

    const tradingWindow = agent?.trading_start_time && agent?.trading_end_time
        ? `${agent.trading_start_time}–${agent.trading_end_time}` : '24 hours';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.4rem', color: '#1e293b' }}>
                    AI Agent Simulator
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    Fake market data over a range of dates, and an agent run across them: positions carry overnight and the
                    results come by date. Nothing reaches the broker.
                </Typography>
            </Box>

            <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth disabled={agentsLoading || agents.length === 0}>
                            <InputLabel sx={{ fontFamily }}>Agent</InputLabel>
                            <Select label="Agent" value={agent ? agentId : ''} sx={{ fontFamily }}
                                onChange={(e) => setAgentId(e.target.value)}>
                                {agents.map((a) => (
                                    <MenuItem key={a.id} value={a.id} sx={{ fontFamily }}>
                                        {a.name} ({a.symbol})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                        {agent && (
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                {agent.execution_mode === 'LLM' ? 'Decides with its active prompt (each bar a Gemini call)' : 'Decides with its rule or the router'}
                                {' · '}trading window {tradingWindow} {agent.trading_timezone || 'America/Chicago'}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}
            {agentsLoading && agents.length === 0 && <Skeleton variant="rectangular" height={320} sx={{ borderRadius: '12px' }} />}
            {!agentsLoading && !error && agents.length === 0 && (
                <Alert severity="info" sx={{ fontFamily }}>No AI agents yet. Create one in AI Trading Agents to simulate it here.</Alert>
            )}

            {agent && (
                <AIAgentDataSimulator
                    key={agent.id}
                    agent={agent}
                    variant="multiDate"
                    canAdd={perms.add}
                    canEdit={perms.edit}
                    canDelete={perms.del}
                    onNotify={setSnackbar}
                    renderSnapshot={(snap) => (
                        <MarketDataSectionGrid sections={buildMarketDataSections(snap.categories, resolveEnabledAgents(agent))} />
                    )}
                />
            )}

            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                {snackbar && <Alert severity={snackbar.severity} sx={{ fontFamily }}>{snackbar.message}</Alert>}
            </Snackbar>
        </Box>
    );
};

export default AIAgentSimulatorPage;
